/**
 * The auth error vocabulary — one map, every auth screen.
 *
 * Three rules govern everything in here:
 *
 * 1. **No user enumeration.** Nothing in this file may reveal whether an
 *    account, email address or clinic exists. Credential failures and
 *    unknown-address failures deliberately collapse onto the same string, and
 *    the forgot-password flow always reports the same neutral confirmation.
 *
 * 2. **Server messages are never rendered.** `resolveAuthError` maps an HTTP
 *    status (plus, where the backend gives one, a machine-readable `code`)
 *    onto a message we authored. A raw `err.response.data.message` reaching
 *    the UI is how enumeration leaks back in through the side door.
 *
 * 3. **These are i18n keys, not English.** Every value below is a key into
 *    `auth.*`; the call site passes it through `t()`. They used to be literal
 *    English strings, which is why the whole reset flow rendered in English on
 *    an Arabic page — and rendered it right-to-left, so trailing full stops
 *    jumped to the front of the sentence. A key cannot do that: the worst case
 *    is that `t()` returns the key, which `pnpm i18n:check` fails the build
 *    over.
 */

export const authMessages = {
  // ── Field-level validation ────────────────────────────────────────────
  emailRequired: 'auth.err.emailRequired',
  emailInvalid: 'auth.err.emailInvalid',
  passwordRequired: 'auth.err.passwordRequired',
  passwordTooShort: 'auth.err.passwordTooShort',
  passwordNoUppercase: 'auth.err.passwordNoUppercase',
  passwordNoLowercase: 'auth.err.passwordNoLowercase',
  passwordNoNumber: 'auth.err.passwordNoNumber',
  passwordNoSymbol: 'auth.err.passwordNoSymbol',
  passwordMismatch: 'auth.err.passwordMismatch',
  /** The server rejected the new password as one already used on the account. */
  passwordReused: 'auth.err.passwordReused',
  otpIncomplete: 'auth.err.otpIncomplete',

  // ── Form-level failures ───────────────────────────────────────────────
  /** Login failure. Intentionally identical for wrong password AND unknown
   *  account — the whole point is that the two are indistinguishable. */
  invalidCredentials: 'auth.err.invalidCredentials',
  tooManyAttempts: 'auth.err.tooManyAttempts',
  accountLocked: 'auth.err.accountLocked',
  accountInactive: 'auth.err.accountInactive',
  emailUnverified: 'auth.err.emailUnverified',
  network: 'auth.err.network',
  server: 'auth.err.server',
  unknown: 'auth.err.unknown',

  // ── Password reset ────────────────────────────────────────────────────
  /** Shown after ANY forgot-password submit, valid address or not. */
  resetLinkSent: 'auth.sent.subtitle',
  resetTokenMissing: 'auth.token.missingBody',
  resetTokenExpired: 'auth.token.expiredBody',
  resetTokenUsed: 'auth.token.usedBody',
  resetTokenInvalid: 'auth.token.invalidBody',
  resetSuccess: 'auth.resetDone.title',

  // ── OTP / email verification ──────────────────────────────────────────
  otpInvalid: 'auth.otp.invalid',
  otpExpired: 'auth.otp.expired',
  otpTooManyAttempts: 'auth.otp.tooMany',
  otpSendFailed: 'auth.otp.sendFailed',
  otpSent: 'auth.otp.sent',
  otpVerified: 'auth.otp.verifiedTitle',

  // ── Clinics ─────────────────────────────────────────────────────
  clinicLoadFailed: 'auth.clinic.loadFailed',
  clinicSelectFailed: 'auth.clinic.selectFailed',
  clinicDeactivated: 'auth.clinic.deactivated',
  clinicDeactivatedHint: 'auth.clinic.deactivatedHint',
  clinicNone: 'auth.clinic.none',
  clinicNoneHint: 'auth.clinic.noneHint',
} as const;

export type AuthMessageKey = keyof typeof authMessages;

/** The shape axios gives us, narrowed to what we are willing to read. */
interface HttpErrorLike {
  response?: { status?: number; data?: { code?: string } };
  code?: string;
  message?: string;
}

/**
 * Backend `code` values we map explicitly. Anything else falls through to the
 * status-based mapping so an unrecognised code can never leak text.
 *
 * Keep this in step with the API. Every code here must actually be emitted by
 * `dentacares-api`; a code listed here but never sent is a branch that looks
 * handled and is not — which is how an expired reset link ended up showing
 * "Something went wrong. Try again." instead of its recovery screen.
 */
const CODE_MAP: Record<string, AuthMessageKey> = {
  STAFF_DEACTIVATED: 'accountInactive',
  ACCOUNT_LOCKED: 'accountLocked',
  EMAIL_NOT_VERIFIED: 'emailUnverified',
  TOKEN_EXPIRED: 'resetTokenExpired',
  TOKEN_USED: 'resetTokenUsed',
  TOKEN_INVALID: 'resetTokenInvalid',
  PASSWORD_REUSED: 'passwordReused',
  OTP_EXPIRED: 'otpExpired',
  OTP_INVALID: 'otpInvalid',
};

/** The machine-readable code on an error, if the backend sent one. */
export function errorCode(error: unknown): string | undefined {
  return (error as HttpErrorLike)?.response?.data?.code;
}

/**
 * Turn an unknown thrown value into an i18n key for a message we wrote.
 *
 * @param fallback which generic message to use when the error is not one we
 *   recognise — e.g. `invalidCredentials` on the login screen, `otpInvalid`
 *   on the OTP screen.
 * @returns a key to pass through `t()`, never server text.
 */
export function resolveAuthError(
  error: unknown,
  fallback: AuthMessageKey = 'unknown',
): string {
  const err = error as HttpErrorLike;
  const code = err?.response?.data?.code;
  if (code && CODE_MAP[code]) return authMessages[CODE_MAP[code]];

  const status = err?.response?.status;

  // No response at all — the request never completed.
  if (status === undefined) {
    if (err?.code === 'ERR_NETWORK' || err?.code === 'ECONNABORTED') {
      return authMessages.network;
    }
    return authMessages[fallback];
  }

  if (status === 429) return authMessages.tooManyAttempts;
  if (status === 423) return authMessages.accountLocked;
  if (status >= 500) return authMessages.server;

  // 400/401/403/404 all collapse onto the caller's generic message. This is
  // the anti-enumeration rule: a 404 "no such user" and a 401 "wrong
  // password" MUST be indistinguishable to the person at the keyboard.
  return authMessages[fallback];
}
